import React, { useContext } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { X, Pencil, FileText } from 'lucide-react'
import ModalContext from '../../contexts/ModalContext'
import UserContext, { Roles } from '../../contexts/UserContext'
import { useProfileContext } from '../../contexts/AccessesContext'
import { UserStatus } from '../../types/userStatus'
import { downloadRemoteFile } from '../../utils/downloadRemoteFile'
import { openSignedAttachment } from '../../utils/openSignedAttachment'
import UserAvatar from '../UserAvatar'
import {
  Container,
  GradientHeader,
  HeaderTop,
  CloseButton,
  AvatarWrap,
  Body,
  NameRow,
  Name,
  Email,
  EditButton,
  BadgesRow,
  StatusBadge,
  ProfileBadge,
  Divider,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  SkillsBox,
  SkillsBoxLabel,
  ChipsWrap,
  Chip,
  EmptySkills,
  SectionTitle,
  AttachmentsList,
  AttachmentRow,
  AttachmentIcon,
  AttachmentName,
  AttachmentLink,
  Footer,
  CloseTextButton,
} from './style'

const ViewAccessUserModal: React.FC = () => {
  const { setModalActive, setModalSection, selectedAccessUser } = useContext(ModalContext)
  const { user } = useContext(UserContext)
  const { profiles } = useProfileContext()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')

  const handleCloseModal = () => {
    setModalActive(false)
    if (returnTo) {
      navigate(returnTo, { replace: true })
    }
  }

  if (!selectedAccessUser) return null

  const isTenantAdmin = user?.role === Roles.tenantAdmin
  const isActive = selectedAccessUser.status === UserStatus.ACTIVE

  const initials = selectedAccessUser.name
    .split(' ')
    .slice(0, 2)
    .map((w: string) => w[0] ?? '')
    .join('')
    .toUpperCase()

  const profileName = profiles.find(p => p.id === selectedAccessUser.profileId)?.name ?? null
  const skills = selectedAccessUser.skills ?? []
  const attachments = selectedAccessUser.attachments ?? []

  const handleEdit = () => {
    setModalSection('editAccessUser')
  }

  return (
    <Container>
      <GradientHeader>
        <HeaderTop>
          <CloseButton onClick={handleCloseModal}>
            <X size={14} />
          </CloseButton>
        </HeaderTop>
        <AvatarWrap>
          <UserAvatar
            src={(selectedAccessUser as any)?.picture}
            name={selectedAccessUser.name}
            size={72}
            fontSize="26px"
            style={{ border: '3px solid white', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
          />
        </AvatarWrap>
      </GradientHeader>

      <Body>
        <NameRow>
          <div>
            <Name>{selectedAccessUser.name}</Name>
            <Email>{selectedAccessUser.email}</Email>
          </div>
          {isTenantAdmin && (
            <EditButton onClick={handleEdit}>
              <Pencil size={12} />
              Editar
            </EditButton>
          )}
        </NameRow>

        <BadgesRow>
          <StatusBadge $active={isActive}>
            {isActive ? '● Ativo' : '● Inativo'}
          </StatusBadge>
          {profileName && <ProfileBadge>{profileName}</ProfileBadge>}
        </BadgesRow>

        <Divider />

        <InfoGrid>
          <InfoItem>
            <InfoLabel>Empresa</InfoLabel>
            <InfoValue>{selectedAccessUser.company || '—'}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Telefone</InfoLabel>
            <InfoValue>{selectedAccessUser.phone || '—'}</InfoValue>
          </InfoItem>
        </InfoGrid>

        <SkillsBox>
          <SkillsBoxLabel>Habilidades</SkillsBoxLabel>
          {skills.length > 0 ? (
            <ChipsWrap>
              {skills.map(s => (
                <Chip key={s.id}>{s.name}</Chip>
              ))}
            </ChipsWrap>
          ) : (
            <EmptySkills>Nenhuma habilidade cadastrada</EmptySkills>
          )}
        </SkillsBox>

        {attachments.length > 0 && (
          <>
            <Divider />
            <SectionTitle>Anexos</SectionTitle>
            <AttachmentsList>
              {attachments.map(a => (
                <AttachmentRow key={a.id}>
                  <AttachmentIcon>
                    <FileText size={14} />
                  </AttachmentIcon>
                  <AttachmentName>{a.title}</AttachmentName>
                  <AttachmentLink
                    href="#"
                    onClick={(event) => {
                      event.preventDefault()
                      void openSignedAttachment(a.fileUrl)
                    }}
                  >
                    Visualizar
                  </AttachmentLink>
                  <AttachmentLink
                    href="#"
                    onClick={(event) => {
                      event.preventDefault()
                      void downloadRemoteFile(a.fileUrl, a.title)
                    }}
                  >
                    Download
                  </AttachmentLink>
                </AttachmentRow>
              ))}
            </AttachmentsList>
          </>
        )}

        <Footer>
          <CloseTextButton onClick={handleCloseModal}>Fechar</CloseTextButton>
        </Footer>
      </Body>
    </Container>
  )
}

export default ViewAccessUserModal
